import { useEffect, useState } from 'react';
import { ScreenHeader } from '../../components/ui/ScreenHeader';
import { FormSection } from '../../components/ui/FormSection';
import { Toggle } from '../../components/ui/Toggle';
import { Toast } from '../../components/ui/Toast';
import { Button } from '../../components/ui/Button';
import { TextField } from '../../components/ui/TextField';
import { AutoSaveStatus } from '../../components/ui/AutoSaveStatus';
import { useAutoSave } from '../../lib/useAutoSave';
import {
  disablePush,
  enablePush,
  isPushEnabled,
  isPushSupported,
  pushPermission,
} from '../../lib/push';
import {
  fetchPushPreferences,
  fetchPushSubscriptions,
  sendTestPush,
  updatePushPreferences,
  type PushPreferences,
} from '../../api/pushApi';

type PushMode = 'off' | 'immediate' | 'digest';

const DEFAULT_PREFS: PushPreferences = {
  enabled: false,
  messages: true,
  signals: true,
  reports: true,
  mentions: true,
  messagesMode: 'immediate',
  signalsMode: 'immediate',
  reportsMode: 'immediate',
  mentionsMode: 'immediate',
  email: false,
  preview: false,
  quietHoursEnabled: false,
  quietHoursStart: '22:00',
  quietHoursEnd: '08:00',
  quietHoursTimezone: 'UTC',
};

export function NotificationsScreen() {
  const supported = isPushSupported();
  const [permission, setPermission] = useState(pushPermission());
  const [subscribed, setSubscribed] = useState(false);
  const [prefs, setPrefs] = useState<PushPreferences>(DEFAULT_PREFS);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');
  const [deviceCount, setDeviceCount] = useState<number | null>(null);
  const [testBusy, setTestBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [quietDraft, setQuietDraft] = useState({
    start: DEFAULT_PREFS.quietHoursStart,
    end: DEFAULT_PREFS.quietHoursEnd,
    timezone: DEFAULT_PREFS.quietHoursTimezone,
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [sub, remote, devices] = await Promise.all([
          isPushEnabled(),
          fetchPushPreferences().catch(() => DEFAULT_PREFS),
          fetchPushSubscriptions().catch(() => null),
        ]);
        if (!alive) return;
        setSubscribed(sub);
        setPrefs(remote);
        setQuietDraft({
          start: remote.quietHoursStart,
          end: remote.quietHoursEnd,
          timezone: remote.quietHoursTimezone,
        });
        setDeviceCount(devices ? devices.count : null);
      } finally {
        if (alive) setBusy(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const quietHoursSave = useAutoSave(
    quietDraft,
    async (v) => {
      setPrefs(
        await updatePushPreferences({
          quietHoursStart: v.start,
          quietHoursEnd: v.end,
          quietHoursTimezone: v.timezone,
        })
      );
    },
    { enabled: !busy && prefs.quietHoursEnabled }
  );

  const sendTest = async () => {
    setTestBusy(true);
    setError('');
    setNotice('');
    try {
      const res = await sendTestPush();
      setDeviceCount(res.deviceCount);
      if (res.deviceCount === 0) {
        setError('No devices are registered for your account yet - toggle notifications off and on again.');
      } else if (!res.allowed) {
        setError('Test queued, but your notification preferences would suppress it. Turn "Messages" on to receive it.');
      } else {
        setNotice(
          `Test notification queued for ${res.deviceCount} device${res.deviceCount === 1 ? '' : 's'}. ` +
            'It should arrive within a few seconds (longer if a burst is already coalescing).'
        );
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not send a test notification.');
    } finally {
      setTestBusy(false);
    }
  };

  const toggleMaster = async (on: boolean) => {
    setBusy(true);
    setError('');
    try {
      if (on) {
        const res = await enablePush();
        if (!res.ok) {
          setPermission(pushPermission());
          setError(
            res.reason === 'denied'
              ? 'Notifications are blocked in your browser settings.'
              : res.reason === 'no-key'
                ? 'Push is not configured for this deployment.'
                : res.reason === 'unsupported'
                  ? 'This browser does not support push notifications.'
                  : res.error || 'Could not enable notifications.'
          );
          return;
        }
        setPermission('granted');
        setSubscribed(true);
        setPrefs(await updatePushPreferences({ enabled: true }));
      } else {
        await disablePush();
        setSubscribed(false);
        // `enabled` is the account-wide "notify me" switch shared with
        // email - only clear it if email isn't relying on it, otherwise
        // just drop the browser subscription above.
        setPrefs(prefs.email ? { ...prefs } : await updatePushPreferences({ enabled: false }));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  const toggleType = async (
    key: 'messages' | 'signals' | 'reports' | 'mentions' | 'preview',
    on: boolean
  ) => {
    const next = { ...prefs, [key]: on };
    setPrefs(next);
    try {
      setPrefs(await updatePushPreferences({ [key]: on }));
    } catch (e) {
      setPrefs(prefs);
      setError(e instanceof Error ? e.message : 'Could not save that preference.');
    }
  };

  // Email is a delivery channel of its own - it doesn't need a browser
  // push subscription, just the account-wide "notify me" switch on, which
  // we flip on implicitly when email is turned on.
  const setMode = async (key: 'messagesMode' | 'signalsMode' | 'reportsMode' | 'mentionsMode', mode: PushMode) => {
    const previous = prefs;
    setPrefs({ ...prefs, [key]: mode } as PushPreferences);
    try {
      setPrefs(await updatePushPreferences({ [key]: mode }));
    } catch (e) {
      setPrefs(previous);
      setError(e instanceof Error ? e.message : 'Could not save that preference.');
    }
  };

  const toggleEmail = async (on: boolean) => {
    const prev = prefs;
    setPrefs({ ...prefs, email: on, enabled: on ? true : prefs.enabled });
    try {
      setPrefs(await updatePushPreferences(on ? { email: true, enabled: true } : { email: false }));
    } catch (e) {
      setPrefs(prev);
      setError(e instanceof Error ? e.message : 'Could not save that preference.');
    }
  };

  const toggleQuietHours = async (on: boolean) => {
    const prev = prefs;
    setPrefs({ ...prefs, quietHoursEnabled: on });
    try {
      setPrefs(await updatePushPreferences({ quietHoursEnabled: on }));
    } catch (e) {
      setPrefs(prev);
      setError(e instanceof Error ? e.message : 'Could not save that preference.');
    }
  };

  const useDeviceTimezone = () => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz) setQuietDraft((d) => ({ ...d, timezone: tz }));
    } catch {
      /* Intl unavailable - leave the field as-is */
    }
  };

  const master = supported && subscribed && prefs.enabled && permission === 'granted';
  // The per-type gates are shared by push and email, so they stay editable
  // whenever either delivery channel is active.
  const typesDisabled = busy || (!master && !prefs.email);

  return (
    <div className="flex flex-col gap-5">
      <ScreenHeader title="Notifications" />

      {!supported && (
        <p className="text-[15px] text-black/50 dark:text-white/50 px-1">
          This browser does not support push notifications. Try a recent Chrome, Firefox, Edge,
          or (on macOS 13+) Safari.
        </p>
      )}

      <FormSection title="Push notifications">
        <Toggle
          label="Send me a notification when I miss activity"
          checked={master}
          onChange={toggleMaster}
          disabled={busy || !supported}
        />
        {permission === 'denied' && supported && (
          <p className="text-xs text-amber-600 dark:text-amber-400">
            Notifications are blocked for this site. Re-enable them in your browser's site
            settings, then toggle this on.
          </p>
        )}
        <p className="text-xs text-black/40 dark:text-white/40">
          You are only notified about messages you did not already see live. A burst of activity
          in one channel arrives as a single notification.
        </p>

        {master && (
          <div className="flex flex-col gap-2 pt-1">
            <Button variant="bordered" onClick={sendTest} loading={testBusy} disabled={busy}>
              Send test notification
            </Button>
            <p className="text-xs text-black/40 dark:text-white/40">
              {deviceCount === null
                ? 'Sends a real notification through the pipeline to this account.'
                : `${deviceCount} device${deviceCount === 1 ? '' : 's'} registered. The test runs through the full delivery pipeline, so your preferences above still apply.`}
            </p>
          </div>
        )}
      </FormSection>

      <FormSection title="Email notifications">
        <Toggle
          label="Also email me when I miss activity"
          checked={prefs.email}
          onChange={toggleEmail}
          disabled={busy}
        />
        <p className="text-xs text-black/40 dark:text-white/40">
          Sent to your account's email address. Works without browser notifications - the email
          has the channel name and a link, never the message text. Same "Notify me about" filters
          below apply.
        </p>
      </FormSection>

      <FormSection title="Notify me about">
        {([
          ['Messages', 'messagesMode'],
          ['Signals', 'signalsMode'],
          ['Reports', 'reportsMode'],
          ['Mentions', 'mentionsMode'],
        ] as const).map(([label, key]) => (
          <label key={key} className="flex items-center justify-between gap-3 text-sm">
            <span>{label}</span>
            <select
              className="rounded-md border border-black/15 bg-transparent px-2 py-1 text-sm dark:border-white/20"
              value={prefs[key]}
              onChange={(e) => setMode(key, e.target.value as PushMode)}
              disabled={typesDisabled}
            >
              <option value="off">Off</option>
              <option value="immediate">Immediate</option>
              <option value="digest">Daily digest</option>
            </select>
          </label>
        ))}
        <p className="text-xs text-black/40 dark:text-white/40">
          Immediate uses the normal burst-coalesced notification path. Daily digest sends one unread
          summary per day; Off suppresses that category.
        </p>
      </FormSection>

      <FormSection title="Quiet hours">
        <Toggle
          label="Mute notifications during a set time window"
          checked={prefs.quietHoursEnabled}
          onChange={toggleQuietHours}
          disabled={busy}
        />
        {prefs.quietHoursEnabled && (
          <div className="flex flex-col gap-3 pt-1">
            <div className="flex gap-3">
              <TextField
                label="Starts"
                type="time"
                value={quietDraft.start}
                onChange={(e) => setQuietDraft((d) => ({ ...d, start: e.target.value }))}
                onBlur={quietHoursSave.flush}
              />
              <TextField
                label="Ends"
                type="time"
                value={quietDraft.end}
                onChange={(e) => setQuietDraft((d) => ({ ...d, end: e.target.value }))}
                onBlur={quietHoursSave.flush}
              />
            </div>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <TextField
                  label="Timezone"
                  value={quietDraft.timezone}
                  onChange={(e) => setQuietDraft((d) => ({ ...d, timezone: e.target.value }))}
                  onBlur={quietHoursSave.flush}
                  placeholder="e.g. America/New_York"
                />
              </div>
              <Button variant="bordered" onClick={useDeviceTimezone}>
                Use my timezone
              </Button>
            </div>
            <AutoSaveStatus status={quietHoursSave.status} onRetry={quietHoursSave.retry} />
            <p className="text-xs text-black/40 dark:text-white/40">
              No push notifications arrive during this window - an overnight window like 22:00 to
              08:00 works. You will still see everything you missed once you open the app; this
              only withholds the notification itself.
            </p>
          </div>
        )}
      </FormSection>

      <FormSection title="Notification content">
        <Toggle
          label="Show the message text in the notification"
          checked={prefs.preview}
          onChange={(v) => toggleType('preview', v)}
          disabled={busy}
        />
        <p className="text-xs text-black/40 dark:text-white/40">
          Off by default: notifications show only the channel and who posted. Turn this on to also
          see the message text or signal value on your lock screen and in emails.
        </p>
      </FormSection>

      {error && <Toast message={error} onDismiss={() => setError('')} />}
      {notice && <Toast message={notice} onDismiss={() => setNotice('')} />}
    </div>
  );
}
